import React, { ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import Topbar from "../Topbar";

import { 
  FooterContainer, 
  PageContainer, 
  PageHeaderContainer, 
  PageTitle,
  ModalInstructionText,
  ModalInstructionSteps,
  ModalInstructionStep,
  ModalErrorAlert
} from "./styles";
import SideBar from "../SideBar";
import GlobalLayoutFooter from "../GlobalLayoutFooter";
import { useAuth } from "../../hooks/AuthContext";
import { useNavigate } from "react-router-dom";
import { RiArrowLeftLine, RiCloseLine, RiNotification2Line } from "react-icons/ri";
import { FormModal } from "../FormModal";
import { toast } from "react-toastify";
import api from "../../services/api";
import { requestFirebaseNotificationPermission } from "../../services/firebase";

interface LayoutProps {
  noPadding?: boolean;
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children, noPadding }) => {
  const { navigationAction } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isIframe, setIsIframe] = useState(false);
  const [isDesktopExpanded, setIsDesktopExpanded] = useState(() => {
    const savedState = localStorage.getItem("@FixitApp:SidebarExpanded");
    return savedState !== null ? JSON.parse(savedState) : true;
  });
  const location = useLocation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalState, setModalState] = useState<"invite" | "instructions">("invite");
  const [isMobile, setIsMobile] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [modalError, setModalErrorMessage] = useState<string | null>(null);

  const isPermissionDenied = "Notification" in window && (Notification.permission as string) === "denied";

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 720);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if ("Notification" in window) {
      const isGranted = Notification.permission === "granted";
      const dismissedAt = localStorage.getItem("@FixitApp:notificationModalDismissedAt");
      const hoursPassed = dismissedAt ? (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60) : 999;
      
      if (!isGranted && hoursPassed >= 24) {
        setIsModalOpen(true);
        setModalState("invite");
      }
    }
  }, []);

  useEffect(() => {
    const handleWindowFocus = async () => {
      if (isModalOpen && "Notification" in window && Notification.permission === "granted" && !isRegistering) {
        setIsRegistering(true);
        try {
          const token = await requestFirebaseNotificationPermission();
          if (token) {
            await api.post("/notifications/token", {
              fcm_token: token,
              device_type: isMobile ? "mobile" : "web",
            });
            toast.success("Notificações ativadas com sucesso!");
            setIsModalOpen(false);
          }
        } catch (error) {
          console.error("Auto-activation error on focus:", error);
        } finally {
          setIsRegistering(false);
        }
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    return () => window.removeEventListener("focus", handleWindowFocus);
  }, [isModalOpen, isMobile, isRegistering]);

  const handleEnableNotifications = async () => {
    if (!("Notification" in window)) {
      toast.warning("Seu navegador não suporta notificações.");
      return;
    }

    setIsRegistering(true);
    setModalErrorMessage(null);

    // Micro-delay artificial de 300ms para dar feedback visual de re-checagem
    await new Promise((resolve) => setTimeout(resolve, 300));

    if ((Notification.permission as string) === "denied") {
      if (modalState === "instructions") {
        setModalErrorMessage(
          "As notificações continuam bloqueadas no seu navegador. Certifique-se de mudar para 'Permitir' no cadeado (🔒) e clique em 'Tentar novamente'."
        );
      } else {
        setModalState("instructions");
      }
      setIsRegistering(false);
      return;
    }

    try {
      const token = await requestFirebaseNotificationPermission();
      if (token) {
        await api.post("/notifications/token", {
          fcm_token: token,
          device_type: isMobile ? "mobile" : "web",
        });
        toast.success("Notificações ativadas com sucesso!");
        setIsModalOpen(false);
      } else {
        if ((Notification.permission as string) === "denied") {
          setModalState("instructions");
        }
      }
    } catch (error) {
      console.error("Error enabling notifications:", error);
      toast.error("Ocorreu um erro ao ativar as notificações.");
    } finally {
      setIsRegistering(false);
    }
  };

  const handleDismissModal = () => {
    localStorage.setItem("@FixitApp:notificationModalDismissedAt", String(Date.now()));
    setModalErrorMessage(null);
    setIsModalOpen(false);
  };

  useEffect(() => {
    localStorage.setItem("@FixitApp:SidebarExpanded", JSON.stringify(isDesktopExpanded));
  }, [isDesktopExpanded]);

  useEffect(() => {
    if (location.pathname === "/saf") {
      setIsIframe(true);
    } else {
      setIsIframe(false);
    }
  }, [location]);

  // Close mobile menu automatically on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <>
      <SideBar 
        isMenuOpen={isMenuOpen} 
        toggleMenu={toggleMenu} 
        extendedMode={isDesktopExpanded} 
        setExtendedMode={setIsDesktopExpanded} 
      />

      <PageContainer
        noPadding={noPadding ?? false}
        isMenuOpen={isMenuOpen}
        isIframe={isIframe}
        isDesktopExpanded={isDesktopExpanded}
      >
        <Topbar 
          isMenuOpen={isMenuOpen} 
          toggleMenu={toggleMenu} 
          extendedMode={isDesktopExpanded} 
          setExtendedMode={setIsDesktopExpanded} 
        />
        <div className="content-area">
          {!!navigationAction && (
            <PageHeaderContainer>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                {!!navigationAction.hasButton && (
                  <>
                    {navigationAction.action === "back" && (
                      <RiArrowLeftLine className="back-btn" onClick={() => navigate(-1)} />
                    )}
                    {navigationAction.action === "function" && (
                      <RiArrowLeftLine className="back-btn" onClick={navigationAction.functionToBeExecuted} />
                    )}
                  </>
                )}
                <PageTitle>{navigationAction.description}</PageTitle>
              </div>

              {navigationAction.customElement && (
                <div className="custom-element-wrapper">
                  {navigationAction.customElement}
                </div>
              )}
            </PageHeaderContainer>
          )}
          {children}
        </div>
        <FooterContainer>
          <GlobalLayoutFooter />
        </FooterContainer>
      </PageContainer>
      <FormModal
        open={isModalOpen}
        onRequestClose={handleDismissModal}
        title={modalState === "invite" ? "Ativar Notificações" : "Como Ativar as Notificações"}
        subtitle={modalState === "invite" ? "Fique por dentro de tudo o que acontece!" : "Siga as instruções para habilitar no seu navegador"}
        saveText={modalState === "invite" ? (isPermissionDenied ? "Como ativar" : "Ativar") : (modalError ? "Tentar novamente" : "Verificar e Concluir")}
        cancelText={modalState === "invite" ? "Agora não" : "Fechar"}
        onSave={modalState === "invite" ? (isPermissionDenied ? () => setModalState("instructions") : handleEnableNotifications) : handleEnableNotifications}
        onCancel={handleDismissModal}
        width={450}
        hideSaveButton={false}
        isSaveLoading={isRegistering}
        isSaveDisabled={isRegistering}
      >
        {modalState === "invite" ? (
          <ModalInstructionText>
            Ative as notificações para receber avisos e atualizações importantes em tempo real sobre os seus pedidos, mensagens e novidades, mesmo quando não estiver com o aplicativo aberto. É rápido, seguro e mantém você sempre atualizado.
          </ModalInstructionText>
        ) : (
          <>
            <ModalInstructionText>
              As notificações foram bloqueadas no seu navegador. Para ativá-las novamente e receber updates em tempo real, siga as instruções abaixo:
            </ModalInstructionText>
            <ModalInstructionSteps>
              <ModalInstructionStep>
                Clique no ícone de <b>Cadeado (🔒)</b> ou de <b>Configurações (⚙️)</b> que fica à esquerda do endereço do site na barra de endereços do seu navegador.
              </ModalInstructionStep>
              <ModalInstructionStep>
                Localize a opção de <b>Notificações</b>.
              </ModalInstructionStep>
              <ModalInstructionStep>
                Mude a permissão para <b>Permitir</b> ou <b>Autorizar</b>.
              </ModalInstructionStep>
              <ModalInstructionStep>
                <b>Não é necessário atualizar a página!</b> Clique em <b>"Verificar e Concluir"</b> para ativar instantaneamente e garantir que suas impressões ativas continuem rodando em total segurança.
              </ModalInstructionStep>
            </ModalInstructionSteps>
            {modalError && (
              <ModalErrorAlert>
                ⚠️ {modalError}
              </ModalErrorAlert>
            )}
          </>
        )}
      </FormModal>
    </>
  );
};

export default Layout;
